import { useEffect, useState } from "react";

import { dismissToast, scrollToError, subscribeToasts } from "~/lib/errorFeedback";
import type { Toast } from "~/lib/errorFeedback";

// The one toast the app raises: a failed submission, carrying the inline error's
// own text (see `lib/errorFeedback.ts`). Mounted once beside the page, outside
// Inertia's page tree, so it survives the page re-rendering under it.
//
// Tapping it goes back to the error, for the reader who scrolled away while it was
// up. `role="status"` rather than `alert`: the inline error is already an alert
// and has been announced; saying it twice would be noise.
//
// Its two strings are keyed off `<html lang>`, which the entrypoint keeps in step
// with the server's `locale` prop — it lives outside the page, so it cannot read
// the prop itself.

const COPY = {
  en: { fallback: "Something went wrong. Please try again.", dismiss: "Dismiss" },
  es: { fallback: "Algo salió mal. Inténtalo de nuevo.", dismiss: "Cerrar" },
};

const VISIBLE_MS = 6000;

export function Toaster() {
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => subscribeToasts(setToast), []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(dismissToast, VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const lang = document.documentElement.lang.startsWith("es") ? "es" : "en";
  const copy = COPY[lang];

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center p-4"
    >
      {toast && (
        <div
          key={toast.id}
          className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-red-200 bg-white px-4 py-3 text-sm text-stone-900 shadow-lg"
        >
          <span aria-hidden className="mt-0.5 size-2 shrink-0 rounded-full bg-red-600" />
          <button
            type="button"
            onClick={() => toast.target && scrollToError(toast.target)}
            className="min-w-0 flex-1 text-left"
          >
            {toast.message ?? copy.fallback}
          </button>
          <button
            type="button"
            onClick={dismissToast}
            aria-label={copy.dismiss}
            className="-m-1 rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <svg aria-hidden viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
